import SportsScoreIcon from '@mui/icons-material/SportsScore'
import {
  Alert,
  AlertColor,
  Box,
  Chip,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { AxiosError } from 'axios'
import { useEffect, useMemo, useRef, useState } from 'react'

import CustomDialog from '~/components/customDialog'
import OFUSimulateStepItem from '~/features/campaigns/components/ofuSimulateStepItem'
import OFUSimulateStepLoading from '~/features/campaigns/components/ofuSimulateStepLoading'
import useOfuSimulate from '~/features/campaigns/hooks/useOfuSimulate'
import {
  ECampaignChannel,
  ECampaignContactStatus,
  ECampaignStatus,
  TCampaignCreateContact,
  TCampaignCreatePayload,
  TCampaignFormFields,
  TCampaignGetContact,
  TCreateOutreachPayload,
  TOfuSimulateContact,
  TOfuSimulateOutreach,
} from '~/features/campaigns/models'
import rem from '~/utils/ui'

type TOFUSimulateStepsProps = {
  isOpen: boolean
  contactData: TCampaignGetContact
  selectedSimulationAgentId: string
  campaign: Partial<TCampaignFormFields>
  handleCancel: () => void
}

export default function OFUSimulateSteps({
  isOpen,
  contactData,
  selectedSimulationAgentId,
  campaign,
  handleCancel,
}: TOFUSimulateStepsProps) {
  const [outreachList, setOutreachList] = useState<TOfuSimulateOutreach[]>([])
  const [contactStatus, setContactStatus] = useState<ECampaignContactStatus>(
    ECampaignContactStatus.new,
  )
  const [followUp, setFollowUp] = useState<string>('')
  // System prompts are constant for the whole flow; held once here and rendered
  // in every outreach's prompt preview.
  const [generationSystemPrompt, setGenerationSystemPrompt] = useState('')
  const [evaluationSystemPrompt, setEvaluationSystemPrompt] = useState('')
  const [simulationError, setSimulationError] = useState<string | null>(null)
  const endOfConversationRef = useRef<HTMLDivElement | null>(null)
  const previousStateRef = useRef<{
    isPending: boolean
    outreachCount: number
    messageCount: number
    contactStatus: ECampaignContactStatus
    followUp: string
  } | null>(null)
  const lastUpdatedOutreachIndexRef = useRef<number | null>(null)
  const { mutate: simulateOfuCampaign, isPending } = useOfuSimulate()
  const theme = useTheme()
  const agentId = selectedSimulationAgentId || ''
  const isCampaignCompleted =
    outreachList.length >= (campaign.ofu_maxReachouts || 1) ||
    contactStatus === ECampaignContactStatus.disqualified ||
    contactStatus === ECampaignContactStatus.completed ||
    contactStatus === ECampaignContactStatus.done
  const isContactDisqualified =
    contactStatus === ECampaignContactStatus.disqualified

  const isCampaignNotStarted =
    contactStatus === ECampaignContactStatus.notStarted

  const getCampaignPayload = () => {
    const rawFacts = (
      campaign as {
        campaignFacts?: { id?: string; fact?: string; text?: string }[]
      }
    ).campaignFacts
    const mappedCampaignFacts = Array.isArray(rawFacts)
      ? rawFacts
          .map((fact) => ({
            id: fact.id ?? '',
            text: fact.text ?? fact.fact ?? '',
          }))
          .filter((fact) => fact.text.length > 0)
      : undefined

    const payload = {
      ...campaign,
      ...(mappedCampaignFacts && mappedCampaignFacts.length > 0
        ? { campaignFacts: mappedCampaignFacts }
        : {}),
      agentId: agentId || '',
      campaignName:
        (campaign.campaignName?.trim() && campaign.campaignName) ||
        'Simulation',
      status: campaign.status ?? ECampaignStatus.draft,
      channel:
        campaign.channel ??
        campaign.ofu_channels?.[0] ??
        ECampaignChannel.email,
      ofu_outreachTime: campaign.ofu_outreachTime,
      endDate: campaign.endDate || '',
    }

    return Object.fromEntries(
      Object.entries(payload).filter(([, v]) => v != null),
    ) as TCampaignCreatePayload
  }

  const getSimulationErrorMessage = (error: unknown) => {
    const axiosError = error as AxiosError<{
      message?: string
      error?:
        | string
        | {
            message?: string
            formErrors?: string[]
            fieldErrors?: Record<string, string | string[]>
          }
    }>
    const errorResponseData = axiosError?.response?.data

    if (typeof errorResponseData === 'string') {
      return errorResponseData
    }

    if (errorResponseData?.message) {
      return errorResponseData.message
    }

    if (typeof errorResponseData?.error === 'string') {
      return errorResponseData.error
    }

    if (
      typeof errorResponseData?.error === 'object' &&
      errorResponseData.error?.message
    ) {
      return errorResponseData.error.message
    }

    const formErrors = errorResponseData?.error?.formErrors ?? []
    if (formErrors.length) {
      return formErrors.join(', ')
    }

    const fieldErrors = errorResponseData?.error?.fieldErrors
    if (fieldErrors && Object.keys(fieldErrors).length) {
      const firstFieldError = Object.values(fieldErrors).find((value) => value)
      if (Array.isArray(firstFieldError) && firstFieldError.length > 0) {
        return firstFieldError[0]
      }
      if (typeof firstFieldError === 'string') {
        return firstFieldError
      }
    }

    if (error instanceof Error && error.message) {
      return error.message
    }

    return 'Something went wrong while running simulation'
  }

  const handleGenerateOutreach = (isRestart?: boolean) => {
    if (!agentId) {
      setSimulationError('Agent ID is required')
      return
    }

    setSimulationError(null)
    simulateOfuCampaign(
      {
        campaign: getCampaignPayload(),
        contact: {
          ...contactData,
          contactMetadata: contactData.contactMetadata ?? {},
          lastName: contactData.lastName ?? '',
        } as TCampaignCreateContact,
        prevOutreaches: isRestart
          ? []
          : (outreachList as unknown as TCreateOutreachPayload[]),
        agentId: agentId || '',
      },
      {
        onSuccess: (data) => {
          setSimulationError(null)
          const {
            outreaches,
            contactStatus: contactStatusData,
            followUp: followUpData,
            generationSystemPrompt: generationSystemPromptData,
            evaluationSystemPrompt: evaluationSystemPromptData,
          } = data as TOfuSimulateContact
          setOutreachList(
            outreaches.map((outreach) => ({
              ...outreach,
              campaignContactId: contactData.id,
            })),
          )
          setContactStatus(contactStatusData)
          setFollowUp(followUpData)
          setGenerationSystemPrompt(generationSystemPromptData ?? '')
          setEvaluationSystemPrompt(evaluationSystemPromptData ?? '')
        },
        onError: (error) => {
          setSimulationError(getSimulationErrorMessage(error))
        },
      },
    )
  }

  useEffect(() => {
    if (contactData) {
      handleGenerateOutreach()
    }
  }, [contactData])

  const alertBoxData = useMemo(() => {
    if (isCampaignCompleted && isContactDisqualified) {
      return {
        label: 'Disqualified',
        severity: 'error',
        chipBgColor: theme.palette.error.main,
        chipTextColor: theme.palette.common.white,
      }
    }

    if (isCampaignCompleted && !isContactDisqualified) {
      return {
        label: 'Completed',
        severity: 'success',
        chipBgColor: theme.palette.success.main,
        chipTextColor: theme.palette.common.white,
      }
    }

    return {
      label: 'Not started',
      severity: 'warning',
      chipBgColor: theme.palette.warning.main,
      chipTextColor: theme.palette.common.white,
    }
  }, [isCampaignCompleted, isContactDisqualified])

  const handleUpdateOutreach = (
    outreach: TOfuSimulateOutreach,
    index: number,
  ) => {
    const updatedOutreachList = [...outreachList]
    updatedOutreachList[index] = outreach
    lastUpdatedOutreachIndexRef.current = index
    setOutreachList(updatedOutreachList)
  }

  const handleRestartSimulation = () => {
    setOutreachList([])
    setContactStatus(ECampaignContactStatus.new)
    setFollowUp('')
    setSimulationError(null)
    handleGenerateOutreach(true)
  }

  const getMessageCount = (outreaches: TOfuSimulateOutreach[]) =>
    outreaches.reduce(
      (total, outreach) => total + (outreach.messages?.length || 0),
      0,
    )

  const scrollConversationEndIntoView = () => {
    requestAnimationFrame(() => {
      endOfConversationRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      })
    })
  }

  useEffect(() => {
    const currentState = {
      isPending,
      outreachCount: outreachList.length,
      messageCount: getMessageCount(outreachList),
      contactStatus,
      followUp,
    }

    const previousState = previousStateRef.current
    if (!previousState) {
      previousStateRef.current = currentState
      return
    }

    const isNewMessageOnLatestOutreach =
      currentState.messageCount > previousState.messageCount &&
      lastUpdatedOutreachIndexRef.current === currentState.outreachCount - 1

    const shouldScroll =
      // loading state while generating next outreach
      (!previousState.isPending && currentState.isPending) ||
      // new outreach appears in the thread
      currentState.outreachCount > previousState.outreachCount ||
      // new simulated messages are added to the latest outreach only
      isNewMessageOnLatestOutreach ||
      // campaign result/status changes
      currentState.contactStatus !== previousState.contactStatus ||
      currentState.followUp !== previousState.followUp

    if (shouldScroll) {
      scrollConversationEndIntoView()
    }

    previousStateRef.current = currentState
    lastUpdatedOutreachIndexRef.current = null
  }, [contactStatus, followUp, isPending, outreachList])

  return (
    <CustomDialog
      isOpen={isOpen}
      onClose={handleCancel}
      onCancel={handleRestartSimulation}
      title="Preview simulation"
      isDisabled={isPending || isCampaignCompleted || isCampaignNotStarted}
      onConfirm={() => handleGenerateOutreach()}
      cancelText="Restart simulation"
      confirmText="Show next outreach"
      maxWidth={800}
      description="Preview simulation"
      shouldKeepCancelEnabled={!isPending}
    >
      <>
        <Stack
          sx={{
            width: '100%',
            overflow: 'hidden',
            gap: 2,
          }}
        >
          {!simulationError && (
            <Alert severity="info" sx={{ boxSizing: 'border-box' }}>
              This is a simulation of the current campaign and no outreach will
              be sent to the contact. Note that the exact messages and timelines
              can be different based on a person&apos;s responses to the emails,
              calls, and texts.
            </Alert>
          )}
          {simulationError && (
            <Alert severity="error" sx={{ boxSizing: 'border-box' }}>
              {simulationError}
            </Alert>
          )}

          {outreachList.map((outreach, index) => (
            <OFUSimulateStepItem
              key={`outreach - ${outreach.scheduledFor || outreach.executedAt}`}
              outreachNumber={index + 1}
              outreach={outreach}
              maxOutreach={campaign.ofu_maxReachouts || 1}
              index={index}
              handleUpdateOutreach={handleUpdateOutreach}
              generationSystemPrompt={generationSystemPrompt}
              evaluationSystemPrompt={evaluationSystemPrompt}
              contact={
                {
                  ...contactData,
                  contactMetadata: contactData.contactMetadata ?? {},
                  lastName: contactData.lastName ?? '',
                  outreaches: outreachList,
                  contactStatus,
                  followUp,
                } as TOfuSimulateContact
              }
              campaign={getCampaignPayload()}
            />
          ))}
          {isPending && <OFUSimulateStepLoading />}
        </Stack>

        {(isCampaignCompleted || isCampaignNotStarted) && followUp && (
          <Alert
            severity={alertBoxData.severity as AlertColor}
            sx={{ boxSizing: 'border-box', mt: 2 }}
          >
            <Stack direction="row" gap={1}>
              <Typography fontWeight={600}>Contact status:</Typography>

              <Chip
                label={alertBoxData.label}
                size="small"
                sx={{
                  fontSize: rem(12),
                  bgcolor: alertBoxData.chipBgColor,
                  color: alertBoxData.chipTextColor,
                }}
              />
            </Stack>
            <Typography>{followUp}</Typography>
          </Alert>
        )}

        {isCampaignCompleted && (
          <Stack
            gap={1}
            textAlign="center"
            alignItems="center"
            justifyContent="center"
          >
            <SportsScoreIcon sx={{ fontSize: rem(40) }} />
            <Typography variant="body1" fontSize={rem(18)}>
              Campaign completed
            </Typography>
            <Typography variant="body2">
              {outreachList.length >= (campaign.ofu_maxReachouts ?? 1)
                ? 'All'
                : ''}{' '}
              {outreachList.length} outreachs have been completed.
            </Typography>
          </Stack>
        )}

        <Box
          ref={endOfConversationRef}
          aria-hidden
          sx={{ width: '100%', height: 0 }}
        />
      </>
    </CustomDialog>
  )
}
